import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChatService } from '../services/chat.service';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatbotComponent {
  readonly chatService = inject(ChatService);
  private readonly sanitizer = inject(DomSanitizer);

  isOpen = signal(false);
  inputText = signal('');

  private readonly messagesEnd = viewChild<ElementRef>('messagesEnd');

  constructor() {
    effect(() => {
      this.chatService.messages();
      this.chatService.loading();
      setTimeout(() => this.scrollToBottom(), 0);
    });
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
  }

  async send(): Promise<void> {
    const text = this.inputText().trim();
    if (!text) return;
    this.inputText.set('');
    await this.chatService.sendMessage(text);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  formatMessage(content: string): SafeHtml {
    // Escape HTML to prevent XSS
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Use a placeholder to avoid double-matching after each replacement pass
    const PH = '\x00WA\x00';

    // Pass 1: Markdown link format [label](https://wa.me/xxx)
    let result = escaped.replace(
      /\[([^\]]+)\]\((https?:\/\/)?wa\.me\/(\d+)\)/g,
      (_m, _label, _proto, number) => `${PH}${number}${PH}`,
    );

    // Pass 2: Bare wa.me URLs (with or without https://)
    result = result.replace(
      /(https?:\/\/)?wa\.me\/(\d+)/g,
      (_m, _proto, number) => `${PH}${number}${PH}`,
    );

    // Pass 3: Convert all placeholders to actual WhatsApp buttons
    result = result.replace(
      /\x00WA\x00(\d+)\x00WA\x00/g,
      (_m, number) =>
        `<a href="https://wa.me/${number}" target="_blank" rel="noopener noreferrer" class="chatbot-wa-btn">💬 Abrir en WhatsApp</a>`,
    );

    // Preserve line breaks
    return this.sanitizer.bypassSecurityTrustHtml(result.replace(/\n/g, '<br>'));
  }

  private scrollToBottom(): void {
    this.messagesEnd()?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
  }
}
